import { isValidCurrency } from './currency';
import { Money } from './money';

import type { Currency } from './currency';

/**
 * DTO
 */
export type MoneyField = {
  cents: string; // integer, NOT a floating point, string to avoid calculating directly with this
  currency: Currency;
};

/**
 * This method rounds the cents of a money field
 * Few tips from Niels:
 * - money field shouldn't do calculations
 * - this method doesn't even say what it is doing
 * - it is implemented in a terribly overcomplex way
 *
 * @deprecated Don't do calculations on a MoneyField you heretic! Use Money class instead.
 */
export const toCentsField = (money: MoneyField): MoneyField => ({
  cents: Money.fromField(money).toCents(),
  currency: money.currency,
});

export const isMoneyField = (value: unknown): value is MoneyField =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as MoneyField).cents === 'string' &&
  typeof (value as MoneyField).currency === 'string' &&
  isValidCurrency((value as MoneyField).currency);

export type FormatMoneyOptions = {
  withoutFraction?: boolean;
  withoutGrouping?: boolean;
  withoutCurrency?: boolean;
};

export const buildNumberFormat = (
  currency: MoneyField['currency'],
  locale: string,
  options: FormatMoneyOptions = {},
): Intl.NumberFormat => {
  const { withoutFraction = false, withoutGrouping = false, withoutCurrency = false } = options;
  let numberFormatOptions: Intl.NumberFormatOptions = { style: 'currency', currency };

  if (withoutFraction) {
    numberFormatOptions = { ...numberFormatOptions, maximumFractionDigits: 0, minimumFractionDigits: 0 };
  }

  if (withoutGrouping) {
    numberFormatOptions = { ...numberFormatOptions, useGrouping: false };
  }

  const resolvedOptions = new Intl.NumberFormat(locale, numberFormatOptions).resolvedOptions();

  const { locale: resolvedLocale, style, ...restOptions } = resolvedOptions;

  if (withoutCurrency) {
    return new Intl.NumberFormat(resolvedLocale, restOptions);
  }

  return new Intl.NumberFormat(resolvedLocale, { style, ...restOptions });
};

export const formatMoney = ({ cents, currency }: MoneyField, locale: string, options?: FormatMoneyOptions): string => {
  return buildNumberFormat(currency, locale, options).format(parseInt(cents, 10) / 100);
};
